import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { ArrowRight, Briefcase, Check, CheckCircle2, Compass, Globe2, Hammer, Landmark, Mic2, Rocket, Sparkles, Star, Wallet, X } from "lucide-react";
import Seo from "../components/Seo";
import { Reveal, Stagger, Item } from "../components/Reveal";
import { IMG } from "../components/images";

const words = ["Innovators", "Builders", "Leaders", "Creators", "Founders"];

const techStack = [
  "Scratch", "HTML", "CSS", "JavaScript", "React", "Node.js",
  "MongoDB", "React Native", "Arduino IoT", "AI APIs", "Python", "Git",
];

const outcomes = [
  {
    age: "Ages 6–9",
    label: "Foundation Builders",
    color: "#f5836b",
    bg: "#fff4f1",
    img: IMG.creativity(700, 520),
    items: [
      "Build interactive stories and games with Scratch",
      "Create their first real webpage",
      "Learn what money is and how to save",
      "Present confidently to a live audience",
    ],
  },
  {
    age: "Ages 9–12",
    label: "Explorers & Problem Solvers",
    color: "#1b2d5e",
    bg: "#ebeff7",
    img: IMG.peers(700, 520),
    items: [
      "Build and deploy a real website on the internet",
      "Write JavaScript apps that solve community problems",
      "Understand compound interest, assets, and equity",
      "Deliver investor-style pitches to a live audience",
    ],
  },
  {
    age: "Ages 13–18",
    label: "Innovators & Founders",
    color: "#4a44b0",
    bg: "#e8ecf5",
    img: IMG.builder(700, 520),
    items: [
      "Build full-stack web and mobile applications",
      "Integrate real AI into products people use",
      "Understand cap tables, venture financing, and startup P&L",
      "Pitch to external judges at a Grand Showcase",
    ],
  },
];

const differentiators = [
  { icon: Rocket, title: "Real projects, not exercises", desc: "Every quarter ends with a deployed product real people can access. No lorem ipsum. No toy exercises. A teenager's capstone is used by real users before the showcase." },
  { icon: Landmark, title: "International standard", desc: "The same technology stack professional developers use worldwide — MERN, React Native, AI APIs, IoT — applied to African problems, in Nigerian context." },
  { icon: Mic2, title: "Public accountability always", desc: "Every quarter ends with a public presentation to parents, community members, and optional external judges. The audience changes how carefully children work." },
  { icon: Wallet, title: "Financial & venture literacy", desc: "By graduation, students understand compound interest, equity, cap tables, startup financing, and how to raise capital — more than most university graduates ever learn." },
  { icon: Compass, title: "Character is curriculum", desc: "Leadership, integrity, discipline, and resilience are assessed on the same rubric as technical skill. Excellence in character is a graduation criterion, not a nice-to-have." },
  { icon: Globe2, title: "Built for Nigerian ambition", desc: "Paystack, Flutterwave, Andela — these were built by Nigerians. The next wave of those founders is between 6 and 18 right now. We are here to form them." },
];

const journeySteps = [
  { phase: "Phase 1", title: "Build with curiosity", ages: "6–9", desc: "Children discover that their ideas can become things. First projects, first presentations, first sense of identity as a creator.", tech: "Scratch · HTML · STEM · Makey Makey" },
  { phase: "Phase 2", title: "Solve real problems", ages: "9–12", desc: "Students move from creating to problem-solving. They deploy real websites, build JavaScript apps, and pitch community solutions.", tech: "HTML · CSS · JavaScript · APIs · Arduino" },
  { phase: "Phase 3", title: "Build full products", ages: "13–18 Early", desc: "Full-stack development. React portfolios deployed on Vercel. Pitch decks. Git discipline. The habits of a professional developer from day one.", tech: "React · Node · MongoDB · RTK Query" },
  { phase: "Phase 4", title: "Ship on every platform", ages: "13–18 Advanced", desc: "Mobile apps, IoT sensor dashboards, and AI-powered tools — all connected to the same backend. Three platforms. One builder.", tech: "React Native · Arduino IoT · AI APIs · RAG" },
  { phase: "Phase 5", title: "Found a company", ages: "13–18 Capstone", desc: "Innovation capstone. User-tested product. Investor pitch to a panel. Cap tables, P&L, venture financing. This is where builders become founders.", tech: "Full Deployment · Venture Financing · Grand Showcase" },
];

/* Reusable eyebrow pill */
const Pill = ({ children }: { children: React.ReactNode }) => (
  <div className="inline-flex rounded-full bg-brand-100 px-4 py-1.5 text-xs font-semibold uppercase tracking-widest text-brand-600">
    {children}
  </div>
);

export default function HomePage() {
  const [wordIndex, setWordIndex] = useState(0);
  const reduce = useReducedMotion();

  useEffect(() => {
    const interval = setInterval(() => setWordIndex((i) => (i + 1) % words.length), 2200);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="overflow-x-hidden">
      <Seo path="/" />

      {/* ══════════════ HERO ══════════════ */}
      <section id="home" className="relative w-full overflow-hidden bg-[linear-gradient(180deg,#f7f8fb_0%,#eef1f6_55%,#eef1f6_100%)]">
        {/* animated blobs */}
        <div className="pointer-events-none absolute -left-20 top-10 h-72 w-72 rounded-full bg-brand-300/40 blur-3xl animate-blob" />
        <div className="pointer-events-none absolute right-0 top-24 h-80 w-80 rounded-full bg-coral-300/30 blur-3xl animate-blob" style={{ animationDelay: "-6s" }} />
        <div className="pointer-events-none absolute bottom-0 left-1/3 h-72 w-72 rounded-full bg-brand-400/25 blur-3xl animate-blob" style={{ animationDelay: "-12s" }} />

        <div className="relative mx-auto grid w-full max-w-7xl items-center gap-10 px-4 py-14 sm:px-6 lg:grid-cols-2 lg:gap-8 lg:px-10 lg:py-20">
          {/* Left: copy */}
          <motion.div
            initial={reduce ? { opacity: 0 } : { opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="text-center lg:text-left"
          >
            <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-brand-200 bg-white/80 px-4 py-2 text-xs font-medium text-ink-mute shadow-sm backdrop-blur sm:text-sm">
              <span className="relative flex h-2.5 w-2.5">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-brand-400 opacity-75" />
                <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-brand-500" />
              </span>
              Now Enrolling · Ages 6–18 · Abuja
            </div>

            <h1 className="text-[2.6rem] font-semibold leading-[0.96] tracking-[-0.05em] text-ink sm:text-[3.4rem] lg:text-[4rem] xl:text-[4.6rem]">
              Raising the next
              <br className="hidden sm:block" /> generation of{" "}
              <span className="relative inline-block">
                <AnimatePresence mode="wait">
                  <motion.span
                    key={words[wordIndex]}
                    initial={reduce ? {} : { opacity: 0, y: 14 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={reduce ? {} : { opacity: 0, y: -14 }}
                    transition={{ duration: 0.4 }}
                    className="inline-block bg-gradient-to-r from-brand-600 via-brand-400 to-coral-400 bg-clip-text text-transparent"
                  >
                    {words[wordIndex]}
                  </motion.span>
                </AnimatePresence>
              </span>
            </h1>

            <p className="mx-auto mt-6 max-w-xl text-base leading-8 text-ink-mute sm:text-lg lg:mx-0">
              A world-class technology, AI, STEM, leadership and entrepreneurship programme that turns curious children into builders who ship real products — and founders who understand how to raise capital.
            </p>

            <div className="mt-8 flex flex-col items-stretch gap-3 sm:flex-row sm:items-center sm:justify-center lg:justify-start">
              <Link to="/enroll" className="group inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-brand-600 to-brand-400 px-7 py-4 text-sm font-semibold text-white shadow-glow transition hover:-translate-y-0.5 hover:shadow-glow-lg">
                Enroll Your Child <ArrowRight size={16} className="transition group-hover:translate-x-0.5" />
              </Link>
              <a href="#curriculum" className="inline-flex items-center justify-center rounded-full border border-brand-200 bg-white px-7 py-4 text-sm font-semibold text-ink transition hover:bg-brand-50">
                Explore Curriculum
              </a>
            </div>

            {/* trust row */}
            <div className="mt-8 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm text-ink-mute lg:justify-start">
              {["12+ deployed projects per graduate", "Public showcases every term", "Character assessed like code"].map((t) => (
                <span key={t} className="inline-flex items-center gap-1.5">
                  <CheckCircle2 size={15} className="text-brand-500" /> {t}
                </span>
              ))}
            </div>
          </motion.div>

          {/* Right: image collage */}
          <motion.div
            initial={reduce ? { opacity: 0 } : { opacity: 0, scale: 0.94, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
            className="relative mx-auto w-full max-w-lg lg:max-w-none"
          >
            <div className="relative overflow-hidden rounded-[36px] border-4 border-white shadow-card">
              <img
                src={IMG.heroKid(900, 1000)}
                alt="A smiling Future Smart Lab learner holding her laptop"
                className="h-[380px] w-full object-cover sm:h-[460px] lg:h-[540px]"
                fetchPriority="high"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-brand-800/30 via-transparent to-transparent" />
            </div>

            {/* floating stat card */}
            <motion.div
              className="absolute -left-3 top-10 rounded-2xl border border-brand-100 bg-white/95 px-4 py-3 shadow-soft backdrop-blur sm:-left-6"
              animate={reduce ? {} : { y: [0, -10, 0] }}
              transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
            >
              <div className="flex items-center gap-2.5">
                <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-brand-500 text-white"><Rocket size={16} /></div>
                <div>
                  <div className="text-lg font-semibold leading-none text-ink">12+</div>
                  <div className="text-[12.5px] text-ink-mute">live projects shipped</div>
                </div>
              </div>
            </motion.div>

            {/* floating badge */}
            <motion.div
              className="absolute -right-2 bottom-10 rounded-2xl border border-coral-300/50 bg-white/95 px-4 py-3 shadow-soft backdrop-blur sm:-right-5"
              animate={reduce ? {} : { y: [0, 10, 0] }}
              transition={{ duration: 6, repeat: Infinity, ease: "easeInOut", delay: 1 }}
            >
              <div className="flex items-center gap-2.5">
                <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-coral-400 text-white"><Briefcase size={16} /></div>
                <div>
                  <div className="text-sm font-semibold leading-tight text-ink">Founder-ready</div>
                  <div className="text-[12.5px] text-ink-mute">cap tables & pitches</div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>

        {/* tech marquee */}
        <div className="relative border-t border-brand-200/60 bg-white/50 py-4 backdrop-blur">
          <div className="mx-auto flex max-w-7xl items-center gap-3 overflow-hidden px-4">
            <span className="hidden flex-shrink-0 text-xs font-semibold uppercase tracking-widest text-ink-mute sm:block">Built with</span>
            <div className="flex overflow-hidden [mask-image:linear-gradient(90deg,transparent,#000_12%,#000_88%,transparent)]">
              <div className="flex flex-shrink-0 animate-marquee items-center gap-3 pr-3">
                {[...techStack, ...techStack].map((t, i) => (
                  <span key={i} className="flex-shrink-0 rounded-full border border-brand-200 bg-white px-4 py-1.5 text-xs font-medium text-brand-600">{t}</span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════ VALUE ══════════════ */}
      <section className="mx-auto w-full max-w-7xl px-4 py-14 sm:px-6 lg:px-10 lg:py-20">
        <div className="overflow-hidden rounded-[34px] border border-brand-200 bg-white p-6 shadow-soft md:p-10">
          <Reveal className="text-center">
            <Pill>The real value</Pill>
            <h2 className="mt-4 text-[2rem] font-semibold tracking-[-0.04em] text-ink sm:text-4xl md:text-5xl">
              Not just a coder.<br />A founder in the making.
            </h2>
            <p className="mx-auto mt-4 max-w-3xl text-base leading-8 text-ink-mute">
              Every parent in Nigeria has seen "coding for kids." This is different. We build children who can start companies, raise capital, and be positioned to build the next generation of African unicorns.
            </p>
          </Reveal>

          <div className="mt-10 grid items-stretch gap-5 lg:grid-cols-[1.1fr_0.9fr]">
            <Stagger className="grid gap-5 sm:grid-cols-1">
              {[
                { icon: Hammer, bg: "bg-brand-500", tint: "bg-brand-50", title: "They will build real products", desc: "By the time a student completes this programme, they will have shipped 12+ real, deployed projects — websites, mobile apps, IoT devices, and AI tools that real users can access. Not exercises. Products." },
                { icon: Briefcase, bg: "bg-coral-400", tint: "bg-coral-100", title: "They will know how to raise capital", desc: "Venture financing, cap tables, equity, pre-seed and seed rounds — students learn what investors actually look for, and how startup financing works from the founder's side. This is not theory." },
                { icon: Globe2, bg: "bg-brand-700", tint: "bg-brand-100", title: "They will be positioned to lead", desc: "The next Paystack, Flutterwave, Andela. The founders behind those companies were once teenagers somewhere. Future Smart Lab exists to make sure the next wave grows up in our programme." },
              ].map((c) => (
                <Item key={c.title}>
                  <div className={`flex gap-4 rounded-[24px] ${c.tint} p-6`}>
                    <div className={`flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-2xl ${c.bg} text-white`}><c.icon size={20} /></div>
                    <div>
                      <h3 className="text-lg font-semibold tracking-[-0.02em] text-ink">{c.title}</h3>
                      <p className="mt-2 text-sm leading-7 text-ink-mute">{c.desc}</p>
                    </div>
                  </div>
                </Item>
              ))}
            </Stagger>

            <Reveal delay={0.15} className="relative min-h-[320px] overflow-hidden rounded-[28px]">
              <img src={IMG.builder(900, 1100)} alt="A teenager creating on his laptop" className="absolute inset-0 h-full w-full object-cover" loading="lazy" />
              <div className="absolute inset-0 bg-gradient-to-t from-brand-800/85 via-brand-800/20 to-transparent" />
              <div className="absolute inset-x-0 bottom-0 p-6 text-white">
                <p className="text-xs font-semibold uppercase tracking-widest text-white/70">From learner to builder</p>
                <p className="mt-2 text-lg font-medium leading-7">"A child who builds will create the systems everyone else uses."</p>
              </div>
            </Reveal>
          </div>

          {/* stats */}
          <Stagger className="mt-8 grid gap-4 rounded-[28px] bg-ink p-6 sm:grid-cols-2 md:grid-cols-4">
            {[
              { stat: "12+", label: "Live deployed projects per graduate" },
              { stat: "3", label: "Platforms shipped: web, mobile, IoT" },
              { stat: "6", label: "Formation strands in every session" },
              { stat: "4", label: "Public showcases per programme stage" },
            ].map((s) => (
              <Item key={s.stat} className="text-center">
                <div className="bg-gradient-to-r from-brand-300 to-coral-300 bg-clip-text text-3xl font-semibold tracking-[-0.04em] text-transparent">{s.stat}</div>
                <div className="mt-1 text-xs leading-5 text-white/60">{s.label}</div>
              </Item>
            ))}
          </Stagger>
        </div>
      </section>

      {/* ══════════════ CURRICULUM ══════════════ */}
      <section id="curriculum" className="mx-auto w-full max-w-7xl px-4 pb-14 sm:px-6 lg:px-10 lg:pb-20">
        <div className="overflow-hidden rounded-[34px] border border-brand-200 bg-white p-6 shadow-soft md:p-10">
          <div className="grid items-center gap-8 lg:grid-cols-2">
            <Reveal className="relative order-2 min-h-[300px] overflow-hidden rounded-[28px] lg:order-1">
              <img src={IMG.classroom(1000, 800)} alt="Girls learning at computers with their teacher" className="absolute inset-0 h-full w-full object-cover" loading="lazy" />
              <div className="absolute inset-0 bg-gradient-to-tr from-brand-600/30 to-transparent" />
            </Reveal>
            <div className="order-1 lg:order-2">
              <Reveal>
                <Pill>The curriculum</Pill>
                <h2 className="mt-4 text-[2rem] font-semibold tracking-[-0.04em] text-ink sm:text-4xl md:text-5xl">A curriculum built for tomorrow</h2>
                <p className="mt-4 max-w-xl text-base leading-8 text-ink-mute">
                  We combine technology, AI, STEM, leadership, entrepreneurship, and value formation to help children grow into capable builders and responsible leaders.
                </p>
              </Reveal>
              <Stagger className="mt-6 grid gap-3 sm:grid-cols-2">
                {[
                  { t: "Technology", d: "Coding, web & mobile creation, and hands-on project work — progressively." },
                  { t: "AI & Innovation", d: "From basic tech exposure into innovation thinking and practical AI." },
                  { t: "STEM Integration", d: "Experiments, data, and engineering thinking woven into every project." },
                  { t: "Leadership & Values", d: "Character, communication, and confidence built on the same rubric." },
                ].map((c) => (
                  <Item key={c.t}>
                    <div className="h-full rounded-2xl border border-brand-100 bg-brand-50 p-5">
                      <h3 className="text-base font-semibold text-ink">{c.t}</h3>
                      <p className="mt-1.5 text-sm leading-6 text-ink-mute">{c.d}</p>
                    </div>
                  </Item>
                ))}
              </Stagger>
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════ JOURNEY ══════════════ */}
      <section className="mx-auto w-full max-w-7xl px-4 pb-14 sm:px-6 lg:px-10 lg:pb-20">
        <div className="overflow-hidden rounded-[34px] border border-brand-200 bg-white p-6 shadow-soft md:p-10">
          <Reveal className="text-center">
            <Pill>The journey from 6 to 18</Pill>
            <h2 className="mt-4 text-[2rem] font-semibold tracking-[-0.04em] text-ink sm:text-4xl md:text-5xl">From curious to founder</h2>
            <p className="mx-auto mt-4 max-w-2xl text-sm leading-7 text-ink-mute">
              Each phase builds deliberately on the last. A child who enters at 6 and completes the programme at 18 leaves as a founder — not a graduate.
            </p>
          </Reveal>

          <div className="mt-10 grid gap-6 lg:grid-cols-[0.85fr_1.15fr]">
            <Reveal className="relative hidden min-h-[420px] overflow-hidden rounded-[28px] lg:block">
              <img src={IMG.roboticsLab(800, 1100)} alt="Two girls building a robot together" className="absolute inset-0 h-full w-full object-cover" loading="lazy" />
              <div className="absolute inset-0 bg-gradient-to-t from-brand-800/80 via-brand-800/10 to-transparent" />
              <div className="absolute inset-x-0 bottom-0 p-6 text-white">
                <p className="text-xs font-semibold uppercase tracking-widest text-white/70">Hands-on, always</p>
                <p className="mt-2 text-lg font-medium leading-7">Building real things, from day one.</p>
              </div>
            </Reveal>

            <Stagger className="relative space-y-4">
              {/* connecting line */}
              <div className="absolute left-[19px] top-4 bottom-4 w-px bg-brand-200" />
              {journeySteps.map((step, i) => (
                <Item key={step.phase}>
                  <div className="relative flex gap-4 rounded-[24px] border border-brand-100 bg-brand-50 p-5">
                    <div className="z-10 flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-brand-600 to-brand-400 text-sm font-bold text-white shadow-glow">{i + 1}</div>
                    <div className="flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-xs font-semibold uppercase tracking-wider text-brand-600">{step.phase}</span>
                        <span className="rounded-full bg-coral-300/30 px-2.5 py-0.5 text-xs font-medium text-[#7a4a40]">Ages {step.ages}</span>
                      </div>
                      <h3 className="mt-1.5 text-lg font-semibold tracking-[-0.02em] text-ink">{step.title}</h3>
                      <p className="mt-1.5 text-sm leading-6 text-ink-mute">{step.desc}</p>
                      <div className="mt-3 inline-flex rounded-full border border-brand-200 bg-white px-3 py-1 text-xs text-brand-600">{step.tech}</div>
                    </div>
                  </div>
                </Item>
              ))}
            </Stagger>
          </div>
        </div>
      </section>

      {/* ══════════════ TRACKS ══════════════ */}
      <section id="tracks" className="mx-auto w-full max-w-7xl px-4 pb-14 sm:px-6 lg:px-10 lg:pb-20">
        <div className="relative overflow-hidden rounded-[34px] bg-gradient-to-br from-brand-400 to-brand-600 px-6 py-10 text-white shadow-card md:px-8">
          <div className="pointer-events-none absolute -right-16 -top-16 h-64 w-64 rounded-full bg-white/10 blur-2xl" />
          <Reveal className="relative text-center">
            <h2 className="text-[2rem] font-semibold tracking-[-0.04em] sm:text-4xl md:text-5xl">Programmes by age and stage</h2>
            <p className="mx-auto mt-3 max-w-3xl text-sm leading-6 text-white/85">
              Two clear pathways help children grow from curiosity to confidence, and from confidence to real-world innovation.
            </p>
          </Reveal>

          <Stagger className="relative mt-10 grid gap-5 md:grid-cols-2">
            {[
              { badge: "AGES 6–12", title: "Junior Builders", img: IMG.peers(800, 500), desc: "Children move from early coding and digital creativity into foundational web skills, problem-solving, STEM exploration, and confidence-building through hands-on projects.", items: ["Scratch and logic", "HTML, CSS & JavaScript foundations", "Early AI and innovation exposure", "Biweekly STEM activities"] },
              { badge: "AGES 13–18", title: "Senior Builders", img: IMG.builder(800, 500), desc: "Teenagers grow into advanced creators through web and mobile development, IoT thinking, and applied AI — alongside venture financing and investor-pitch training.", items: ["Full-stack development", "Mobile app building", "IoT & engineering exposure", "Applied AI projects", "Startup financing & investor pitch"] },
            ].map((tk) => (
              <Item key={tk.title}>
                <div className="overflow-hidden rounded-[28px] bg-white/15 backdrop-blur-sm">
                  <div className="relative h-40 overflow-hidden">
                    <img src={tk.img} alt={tk.title} className="h-full w-full object-cover" loading="lazy" />
                    <div className="absolute inset-0 bg-gradient-to-t from-brand-600/70 to-transparent" />
                    <span className="absolute left-4 top-4 inline-flex rounded-full bg-white/25 px-3 py-1 text-xs font-medium backdrop-blur">{tk.badge}</span>
                  </div>
                  <div className="p-6">
                    <h3 className="text-2xl font-semibold tracking-[-0.02em]">{tk.title}</h3>
                    <p className="mt-3 text-sm leading-7 text-white/85">{tk.desc}</p>
                    <ul className="mt-5 space-y-2 text-sm text-white/85">
                      {tk.items.map((it) => (
                        <li key={it} className="flex items-center gap-2"><CheckCircle2 size={15} className="flex-shrink-0 text-white/80" /> {it}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </Item>
            ))}
          </Stagger>
        </div>
      </section>

      {/* ══════════════ OUTCOMES ══════════════ */}
      <section className="mx-auto w-full max-w-7xl px-4 pb-14 sm:px-6 lg:px-10 lg:pb-20">
        <div className="overflow-hidden rounded-[34px] border border-brand-200 bg-white p-6 shadow-soft md:p-10">
          <Reveal className="text-center">
            <Pill>Age-by-age outcomes</Pill>
            <h2 className="mt-4 text-[2rem] font-semibold tracking-[-0.04em] text-ink sm:text-4xl">What your child will be able to do</h2>
            <p className="mx-auto mt-3 max-w-2xl text-sm leading-7 text-ink-mute">These are not aspirations. They are the minimum standard for completing each programme phase.</p>
          </Reveal>

          <Stagger className="mt-10 grid gap-5 md:grid-cols-3">
            {outcomes.map((o) => (
              <Item key={o.age}>
                <div className="h-full overflow-hidden rounded-[28px]" style={{ background: o.bg }}>
                  <div className="relative h-40 overflow-hidden">
                    <img src={o.img} alt={o.label} className="h-full w-full object-cover" loading="lazy" />
                    <span className="absolute left-4 top-4 inline-flex rounded-full px-3 py-1 text-xs font-semibold text-white" style={{ background: o.color }}>{o.age}</span>
                  </div>
                  <div className="p-6">
                    <h3 className="text-lg font-semibold tracking-[-0.02em] text-ink">{o.label}</h3>
                    <ul className="mt-4 space-y-3">
                      {o.items.map((item) => (
                        <li key={item} className="flex items-start gap-2.5 text-sm leading-6 text-ink-mute">
                          <span className="mt-0.5 flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full text-[11.5px] font-bold text-white" style={{ background: o.color }}><Check className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden /></span>
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </Item>
            ))}
          </Stagger>
        </div>
      </section>

      {/* ══════════════ SHOWCASE BAND ══════════════ */}
      <section className="mx-auto w-full max-w-7xl px-4 pb-14 sm:px-6 lg:px-10 lg:pb-20">
        <div className="grid items-stretch gap-5 lg:grid-cols-2">
          <Reveal className="relative min-h-[300px] overflow-hidden rounded-[34px]">
            <img src={IMG.showcase(1000, 800)} alt="A girl building her project on a laptop" className="absolute inset-0 h-full w-full object-cover" loading="lazy" />
            <div className="absolute inset-0 bg-gradient-to-t from-ink/85 via-ink/30 to-transparent" />
            <div className="absolute inset-x-0 bottom-0 p-8 text-white">
              <div className="mb-3 inline-flex items-center gap-1.5 rounded-full bg-white/15 px-3 py-1 text-xs font-medium backdrop-blur"><Mic2 size={13} /> Grand Showcase</div>
              <h3 className="text-2xl font-semibold tracking-[-0.02em] sm:text-3xl">Every term ends on a stage</h3>
              <p className="mt-2 max-w-md text-sm leading-7 text-white/75">Children present real, deployed products to parents, community members and external judges. Public accountability is how excellence becomes a habit.</p>
            </div>
          </Reveal>
          <Reveal delay={0.1} className="relative min-h-[300px] overflow-hidden rounded-[34px]">
            <img src={IMG.team(1000, 800)} alt="Two girls working out a problem together at a computer" className="absolute inset-0 h-full w-full object-cover" loading="lazy" />
            <div className="absolute inset-0 bg-gradient-to-t from-brand-700/85 via-brand-700/25 to-transparent" />
            <div className="absolute inset-x-0 bottom-0 p-8 text-white">
              <div className="mb-3 inline-flex items-center gap-1.5 rounded-full bg-white/15 px-3 py-1 text-xs font-medium backdrop-blur"><Sparkles size={13} /> Real mentorship</div>
              <h3 className="text-2xl font-semibold tracking-[-0.02em] sm:text-3xl">Guided by builders, not lecturers</h3>
              <p className="mt-2 max-w-md text-sm leading-7 text-white/80">Small cohorts, working developers as mentors, and a stack used by professionals worldwide — applied to problems that matter here.</p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ══════════════ WHY DIFFERENT ══════════════ */}
      <section id="mission" className="mx-auto w-full max-w-7xl px-4 pb-14 sm:px-6 lg:px-10 lg:pb-20">
        <div className="relative overflow-hidden rounded-[34px] bg-ink p-6 text-white shadow-card md:p-10">
          <div className="pointer-events-none absolute -left-10 top-0 h-72 w-72 rounded-full bg-brand-600/30 blur-3xl" />
          <div className="pointer-events-none absolute -right-10 bottom-0 h-72 w-72 rounded-full bg-coral-400/20 blur-3xl" />
          <Reveal className="relative text-center">
            <p className="text-[2rem] font-semibold tracking-[-0.04em] sm:text-4xl md:text-5xl">Why parents choose this programme</p>
            <p className="mx-auto mt-4 max-w-3xl text-sm leading-7 text-white/70">
              Because children are not only taught what to know. They are taught how to think, how to lead, how to create value, how to raise capital, and how to solve meaningful problems — at international standard.
            </p>
          </Reveal>

          <Stagger className="relative mt-10 grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
            {differentiators.map((d) => (
              <Item key={d.title}>
                <div className="group h-full rounded-[28px] border border-white/10 bg-white/5 p-6 transition hover:bg-white/10">
                  <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-brand-500 to-brand-700 text-white transition group-hover:scale-105"><d.icon size={20} /></div>
                  <h3 className="text-lg font-semibold tracking-[-0.02em]">{d.title}</h3>
                  <p className="mt-2.5 text-sm leading-7 text-white/65">{d.desc}</p>
                </div>
              </Item>
            ))}
          </Stagger>

          <Reveal className="relative mt-8 rounded-[28px] border border-white/10 bg-white/5 p-6 md:p-8">
            <div className="grid gap-8 md:grid-cols-2">
              <div>
                <p className="text-xs font-semibold uppercase tracking-widest text-white/40">A regular coding class</p>
                <ul className="mt-4 space-y-2.5 text-sm text-white/55">
                  {["Teaches coding basics", "Ends when the class ends", "Assessment is a quiz", "No public presentation", "No financial literacy", "No venture or startup training"].map((t) => (
                    <li key={t} className="flex items-center gap-2"><span className="text-white/30"><X className="h-4 w-4" aria-hidden /></span> {t}</li>
                  ))}
                </ul>
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-widest text-brand-300">Future Smart Lab</p>
                <ul className="mt-4 space-y-2.5 text-sm text-white/85">
                  {["Builds real, deployed products people use", "Formation that follows the child for life", "Assessment is a public showcase", "Every term ends with a live presentation", "Financial and investment literacy built in", "Graduates understand cap tables & investor pitches"].map((t) => (
                    <li key={t} className="flex items-center gap-2"><CheckCircle2 size={15} className="flex-shrink-0 text-brand-300" /> {t}</li>
                  ))}
                </ul>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ══════════════ FOUNDER QUOTE ══════════════ */}
      <section className="mx-auto w-full max-w-7xl px-4 pb-14 sm:px-6 lg:px-10 lg:pb-20">
        <Reveal className="overflow-hidden rounded-[34px] border border-brand-200 bg-brand-50 p-8 shadow-soft md:p-12">
          <div className="mx-auto max-w-3xl text-center">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-white text-2xl text-brand-400 shadow-sm">“</div>
            <p className="mt-5 text-xl font-medium leading-9 tracking-[-0.01em] text-ink sm:text-2xl">
              There is a very clear division forming in every economy in the world. On one side are the people who understand how technology works — who can build with it, shape it, direct it. On the other side are the people who use what those builders make. A child who only consumes will spend their career inside systems built by someone else. A child who builds will create those systems.
            </p>
            <div className="mt-8 flex items-center justify-center gap-3">
              <div className="h-px w-10 bg-brand-300" />
              <p className="text-sm font-semibold text-brand-600">Future Smart Lab · Our Philosophy</p>
              <div className="h-px w-10 bg-brand-300" />
            </div>
          </div>
        </Reveal>
      </section>

      {/* ══════════════ CTA ══════════════ */}
      <section className="mx-auto w-full max-w-7xl px-4 pb-16 sm:px-6 lg:px-10 lg:pb-24">
        <Reveal className="relative overflow-hidden rounded-[34px] bg-gradient-to-br from-coral-400 to-coral-500 px-6 py-12 text-white shadow-card md:px-10 md:py-16">
          <div className="pointer-events-none absolute -right-10 -top-10 h-56 w-56 rounded-full bg-white/20 blur-2xl" />
          <div className="pointer-events-none absolute -left-10 bottom-0 h-56 w-56 rounded-full bg-brand-600/30 blur-2xl" />
          <div className="relative mx-auto max-w-3xl text-center">
            <div className="mb-4 inline-flex items-center gap-1.5 rounded-full bg-white/20 px-4 py-1.5 text-xs font-semibold backdrop-blur">
              {[...Array(5)].map((_, i) => <Star key={i} size={12} className="fill-white text-white" />)}
              <span className="ml-1">Now enrolling for the new term</span>
            </div>
            <h2 className="text-[2.3rem] font-semibold leading-[0.98] tracking-[-0.04em] sm:text-[3rem] md:text-[3.6rem]">
              Give your child a future-ready advantage.
            </h2>
            <p className="mx-auto mt-5 max-w-2xl text-base leading-8 text-white/90">
              Start a learning journey that includes technology, AI, STEM, leadership, entrepreneurship, venture thinking, and strong values. Because the next great African founder might be sitting in your house right now.
            </p>
            <div className="mt-8 flex flex-col items-center gap-3 sm:flex-row sm:justify-center">
              <Link to="/enroll" className="inline-flex items-center gap-2 rounded-full bg-ink-deep px-8 py-4 text-sm font-semibold text-white shadow-lg transition hover:-translate-y-0.5 hover:bg-ink">
                Enroll Your Child Now <ArrowRight size={16} />
              </Link>
              <a href="#curriculum" className="inline-flex rounded-full border border-white/60 bg-transparent px-8 py-4 text-sm font-semibold text-white transition hover:bg-white/15">
                Explore the Programme
              </a>
            </div>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
